import {
	ApolloCache,
	DefaultContext,
	FetchResult,
	MutationFunctionOptions,
	OperationVariables,
} from '@apollo/client';
import { navigate } from 'gatsby';
import React, { FC } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import TextButton from '@ui/atoms/TextButton';
import Toast from '@ui/atoms/Toast';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import { IPageProps } from '@folx-types/flow-steps';

import Winback from '@assets/vectors/winback.inline.svg';

import theme from '@theme/FolxTheme';

export interface ICancelationWinbackPageContent {
	pageHeader: string;
	pageInfo: string;
	stayLabel: string;
	cancelLabel: string;
	supportCancel: string;
	supportWinback: string;
}

export interface ICancelationWinbackProps extends IPageProps {
	applyWinbackOffer: (
		options?:
			| MutationFunctionOptions<unknown, OperationVariables, DefaultContext, ApolloCache<unknown>>
			| undefined
	) => Promise<FetchResult<unknown>>;
	cancelMembership: (
		options?:
			| MutationFunctionOptions<unknown, OperationVariables, DefaultContext, ApolloCache<unknown>>
			| undefined
	) => Promise<FetchResult<unknown>>;
	pageContent: ICancelationWinbackPageContent;
}

const AlignedColumn = aligned<IFlexColumn>(fullWidth<IFlexColumn>(FlexColumn));
const FullWidthButton = fullWidth<IButtonProps>(Button);

const WinbackHero = styled.div`
	aspect-ratio: 5 / 2;
	background: ${theme.gradients.cta};
	margin-bottom: ${theme.spacing.sm};
	margin-top: -${theme.spacing.md};
	max-height: 32rem;
	padding: ${theme.spacing.sm} 0 ${theme.spacing.md};
	width: 100vw;
`;

const InnerContainer = styled.div`
	align-items: center;
	display: flex;
	height: 100%;
	justify-content: center;
	width: 100%;

	svg {
		height: 100%;
		width: fit-content;
	}
`;

const CancelationWinback: FC<ICancelationWinbackProps> = ({
	applyWinbackOffer,
	cancelMembership,
	pageContent,
}) => {
	const { pageHeader, pageInfo, stayLabel, cancelLabel, supportWinback, supportCancel } =
		pageContent;

	const applyWinback = async () => {
		await applyWinbackOffer()
			.then(() => {
				navigate('..', {
					state: {
						winback: true,
					},
				});
			})
			.catch(() => {
				toast.error(
					<WYSIWYGContent
						color="white"
						linkColor="white"
						dangerouslySetInnerHTML={{ __html: supportWinback }}
					/>
				);
			});
	};

	const cancelMemberSubscription = async () => {
		await cancelMembership({
			variables: {
				authenticatedMember: true,
			},
		})
			.then(() => {
				navigate('../cancel_survey');
			})
			.catch(() => {
				toast.error(
					<WYSIWYGContent
						color="white"
						linkColor="white"
						dangerouslySetInnerHTML={{ __html: supportCancel }}
					/>
				);
			});
	};

	return (
		<>
			<Toast
				position="top-center"
				theme="colored"
				pauseOnHover
				pauseOnFocusLoss={false}
				autoClose={7500}
			/>
			<WinbackHero>
				<InnerContainer>
					<Winback aria-label="40% Off" alt="40% Off" />
				</InnerContainer>
			</WinbackHero>
			<AlignedColumn gap="lg" alignItems="center">
				<FlexColumn gap="sm">
					<HeadingText>{pageHeader}</HeadingText>
					<BasicText>{pageInfo}</BasicText>
				</FlexColumn>
				<FullWidthButton
					variant="secondary"
					type="button"
					label={stayLabel}
					onClick={async () => {
						await applyWinback();
					}}
				/>
				<TextButton
					variant="text"
					underline
					label={cancelLabel}
					onClick={async () => {
						await cancelMemberSubscription();
					}}
				/>
			</AlignedColumn>
		</>
	);
};

export default CancelationWinback;
